import "./_style.less";
import { useTranslation } from "react-i18next";
import { useBookingAssessment } from "../../providers/booking-assessment.provider";
import {
  Alert,
  Col,
  Collapse,
  Divider,
  Form,
  Input,
  message,
  Radio,
  Row,
  Skeleton,
  Space,
  Typography,
} from "antd";
import {
  VuiButton,
  VuiContentLoading,
  VuiDatePicker,
  VuiFormCard,
} from "../../../../@vendor/components";
import {
  InfoCircleOutlined,
  LeftOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { ArrowDownIcon, PatientModal } from "../../../../components";
import { useGetPatients } from "../../../patients/hooks/use-get-patients";
import PatientCard from "../../../patients/components/patient-card";
import { Patient } from "../../../../models/Patient";
import {
  sortScheduleDateOptions,
  sortScheduleTimeOptions,
} from "../../../../@vendor/utils";
import SessionCard from "../../../booking-counseling/components/session-card";
import { useGetAssessmentSchedule } from "../../../schedule/hooks/use-get-assessment-schedule";
import ScheduleChip from "../../../booking-counseling/components/schedule-chip";
import _ from "lodash";
import CheckCircleFilledIcon from "../../../../components/icons/CheckCircleFilled";
import PhoneInput from "../../../../@vendor/components/atoms/PhoneInput";
import { DEFAULT_PHONE_COUNTRY_CODE } from "../../../../constants";
import { CountryPhoneInputValue } from "antd-country-phone-input";
import { useWindowSize } from "usehooks-ts";
import { useGuestFieldValidation } from "../../hooks/use-guest-field-validation";
import { useAppSelector } from "../../../../stores/hooks";
import { useNavigate } from "react-router-dom";
import { useStepTransition } from "../../hooks/use-step-transition";
import AssessmentScheduleCalendar from "../assessment-schedule-calendar";
import {
  clearBookingAssessmentInfo,
  getBookingAssessmentInfo,
  saveBookingAssessmentInfo,
} from "../../../../@vendor/utils/helpers/local-storage.helper";

const BookingAssessmentStepPatient = () => {
  const { t } = useTranslation();
  const { account } = useAppSelector((state) => state.system);
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const navigate = useNavigate();
  const bookingAssessmentInfo = getBookingAssessmentInfo();
  const {
    currentStep,
    form,
    onPrevStep,
    setStep,
    triggerValidationError,
    manageStepValidator,
  } = useBookingAssessment();
  const { values, setFieldValue, resetForm, setFormValues } = form;
  const { handleMethodChange, clearScheduleData } = useStepTransition({
    values,
    setFieldValue,
    resetForm,
  });

  const [showPatientModal, setPatientModalVisible] = useState<Patient>();
  const [alertMessage, setAlertMessage] = useState<string>();
  const [activeSessionKey, setActiveSessionKey] = useState<
    string | number | (string | number)[]
  >();
  const [incompletePanels, setIncompletePanels] = useState<
    Record<number, boolean>
  >({});

  const [continueAsGuest, setContinueAsGuest] = useState<boolean>(false);
  const [formGuest] = Form.useForm<Patient>();
  const phone = Form.useWatch(
    ["patient", "phone_number"],
    formGuest
  ) as CountryPhoneInputValue;

  const { emailValidation, phoneValidation } = useGuestFieldValidation({
    email: values?.patient?.email,
    phoneValue: phone,
    manageStepValidator,
    step: 1,
    enabled: continueAsGuest,
  });

  const handleShowPatientForm = (patient: Patient) => {
    setPatientModalVisible(patient);
  };

  const handleClosePatientForm = () => {
    setPatientModalVisible(undefined);
  };

  const {
    schedules,
    assessmentSchedules,
    isFetching,
    sessionScheduleTimes,
    fetchingSessionScheduleTimeIdx,
    loadData,
    loadSessionScheduleTime,
  } = useGetAssessmentSchedule();

  const sortedSchedules = sortScheduleDateOptions(
    Array.isArray(schedules) ? schedules : []
  );

  const sessionCount = assessmentSchedules.length;
  const isSingleSession = sessionCount === 1;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const dateObj = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = isMobile
      ? { weekday: "short", day: "2-digit", month: "short" }
      : { weekday: "long", day: "2-digit", month: "long", year: "numeric" };
    return dateObj.toLocaleDateString(undefined, options as any);
  };

  const {
    data: patients,
    isLoading: isLoadingPatients,
    loadData: loadPatients,
  } = useGetPatients();

  useEffect(() => {
    if (
      currentStep === 1 &&
      values?.assessment?.id &&
      values?.psychologist?.id &&
      values?.type
    ) {
      loadData({
        assessment_id: values?.assessment?.id,
        psychologist_id: values?.psychologist?.id,
        type: values?.type,
      }).then();
    }
  }, [
    currentStep,
    values?.assessment?.id,
    values?.psychologist?.id,
    values?.type,
    loadData,
  ]);

  useEffect(() => {
    if (currentStep === 1) {
      if (!account) {
        setFieldValue("patient", null);
      } else {
        loadPatients();
      }
    }
  }, [currentStep, account, setFieldValue, loadPatients]);

  useEffect(() => {
    if (values?.assessment?.has_schedule === true) {
      setFieldValue("type", "Online");
    }
  }, [values?.assessment?.has_schedule, setFieldValue]);

  useEffect(() => {
    if (assessmentSchedules && assessmentSchedules.length > 0) {
      setFieldValue("assessmentSchedules", assessmentSchedules);
    }
  }, [assessmentSchedules, setFieldValue]);

  const handleSelectPatient = (patient: Patient) => {
    if (values?.patient?.id === patient.id) {
      setFieldValue("patient", null);
      setFieldValue("patient_id", null);
      setAlertMessage(undefined);
      return;
    }

    setFieldValue("patient", patient);
    setFieldValue("patient_id", patient.id);
    // if selecting an existing patient, ensure guest flow is off
    setContinueAsGuest(false);
    setFieldValue("continueAsGuest", false);
    setAlertMessage(undefined);
  };

  const hasPsychologist = !!values?.psychologist;

  const handleChangePsychologist = () => {
    setFieldValue("psychologist_id", null);
    setFieldValue("psychologist", null);
    setFieldValue("patient", null);
    setFieldValue("schedule_ids", []);
    setFieldValue("schedule", []);
    setFieldValue("time_ids", []);
    setFieldValue("time", []);
    setFieldValue("type", "Online");

    onPrevStep();
  };

  useEffect(() => {
    if (triggerValidationError && currentStep === 1) {
      // Check for incomplete sessions and mark them as incomplete
      const newIncompletePanels: Record<number, boolean> = {};

      if (values?.schedule_ids && values?.time_ids) {
        values.schedule_ids.forEach((scheduleId, index) => {
          const hasSchedule = scheduleId !== undefined && scheduleId !== null;
          const hasTime =
            values.time_ids &&
            values.time_ids[index] !== undefined &&
            values.time_ids[index] !== null;

          if (hasSchedule && !hasTime) {
            newIncompletePanels[index] = true;
          }
        });
      }

      setIncompletePanels(newIncompletePanels);
    }
  }, [
    triggerValidationError,
    currentStep,
    values?.schedule_ids,
    values?.time_ids,
  ]);

  useEffect(() => {
    if (currentStep !== 1) return;
    if (triggerValidationError) return;

    let activeIdx: number | undefined;
    if (Array.isArray(activeSessionKey) && activeSessionKey.length > 0)
      activeIdx = Number(activeSessionKey[0]);
    else if (activeSessionKey !== undefined && activeSessionKey !== null)
      activeIdx = Number(activeSessionKey);

    if (activeIdx === undefined || Number.isNaN(activeIdx)) {
      return;
    }

    const activeIndex = activeIdx as number;

    // Merge with existing incomplete panels so flags persist until a time is selected
    setIncompletePanels((prev) => {
      const merged: Record<number, boolean> = { ...(prev || {}) };
      if (Array.isArray(values?.schedule_ids)) {
        values.schedule_ids.forEach((scheduleId, index) => {
          if (index < activeIndex) {
            const hasSchedule = scheduleId !== undefined && scheduleId !== null;
            const hasTime =
              Array.isArray(values?.time_ids) &&
              values.time_ids[index] !== undefined &&
              values.time_ids[index] !== null;
            if (hasSchedule && !hasTime) {
              merged[index] = true;
            } else {
              // only clear if a time is now selected
              if (hasTime && merged[index]) delete merged[index];
            }
          }
        });
      }
      return merged;
    });
  }, [
    values?.schedule_ids,
    values?.time_ids,
    currentStep,
    activeSessionKey,
    triggerValidationError,
  ]);

  useEffect(() => {
    if (currentStep === 1) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);

  useEffect(() => {
    // keep booking form aware of guest flow state and clear when leaving the step
    setFieldValue("continueAsGuest", continueAsGuest);
    if (currentStep !== 1 && continueAsGuest) {
      setContinueAsGuest(false);
      setFieldValue("continueAsGuest", false);
      formGuest.resetFields();
    }
  }, [currentStep, continueAsGuest, setFieldValue, formGuest]);

  useEffect(() => {
    if (bookingAssessmentInfo) {
      setStep(1);
      setFormValues(bookingAssessmentInfo);
      if (bookingAssessmentInfo?.assessment?.number_of_sessions === 1) {
        const date =
          bookingAssessmentInfo?.schedule?.length > 0
            ? bookingAssessmentInfo?.schedule[0]?.date
            : null;
        if (date) {
          loadSessionScheduleTime(
            0,
            date,
            60,
            bookingAssessmentInfo?.psychologist?.id,
            bookingAssessmentInfo?.type ?? ""
          ).then();
        }
      } else if (
        bookingAssessmentInfo?.assessment?.number_of_sessions > 1 &&
        bookingAssessmentInfo?.schedule?.length > 0
      ) {
        for (const [index, schedule] of bookingAssessmentInfo.schedule) {
          loadSessionScheduleTime(
            index,
            schedule.date,
            60,
            bookingAssessmentInfo?.psychologist?.id,
            bookingAssessmentInfo?.type ?? ""
          ).then();
        }
      }

      clearBookingAssessmentInfo();
    }
  }, [values, bookingAssessmentInfo]);

  if (currentStep !== 1) {
    return null;
  }

  const isScheduleDisabled = (schedule: {
    id: number | string;
    date: string;
  }) => {
    const exists =
      Array.isArray(values.schedule_ids) &&
      values.schedule_ids.includes(Number(schedule.id));
    if (exists) return false;
    if (!Array.isArray(values.schedule_ids) || values.schedule_ids.length === 0)
      return false;
    try {
      const candidateTime = new Date(schedule.date).getTime();
      const existingDatesTimes = values.schedule_ids
        .map((id: number) => schedules.find((sc) => sc.id === id)?.date)
        .filter((d): d is string => !!d)
        .map((d) => new Date(d).getTime())
        .filter((t) => !Number.isNaN(t));
      if (existingDatesTimes.length > 0) {
        const maxExisting = Math.max(...existingDatesTimes);
        if (!Number.isNaN(candidateTime) && candidateTime < maxExisting)
          return true;
      }
    } catch (err) {}

    for (let i = 0; i < values.schedule_ids.length; i++) {
      const selectedId = values.schedule_ids[i];
      const selectedSchedule = schedules.find((sc) => sc.id === selectedId);
      const selectedDate = selectedSchedule
        ? new Date(selectedSchedule.date)
        : null;
      const candidateDate = new Date(schedule.date);
      const minDays = assessmentSchedules?.[i]?.session_min_days || 0;
      if (
        selectedDate &&
        !isNaN(candidateDate.getTime()) &&
        !isNaN(selectedDate.getTime())
      ) {
        const diffDays = Math.abs(
          (candidateDate.getTime() - selectedDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );
        if (diffDays < minDays) return true;
      }
    }
    return false;
  };

  const handlePatientModalSubmit = (type: string) => {
    loadPatients();

    setAlertMessage(
      type === "create"
        ? t("common.text.newPatientAddedSuccessfully")
        : t("common.text.profileUpdatedSuccessfully")
    );

    setTimeout(() => {
      setAlertMessage(undefined);
    }, 2000);
  };

  const handleSelectOnlineSession = () => {
    handleMethodChange("Online");
    setActiveSessionKey(undefined);
    setIncompletePanels({});
  };

  const handleSelectOfflineSession = () => {
    handleMethodChange("Offline");
    setActiveSessionKey(undefined);
    setIncompletePanels({});
  };

  const handleSingleSessionScheduleClick = (s: any) => {
    const scheduleIdx = schedules.findIndex((sc) => sc.id === s.id);
    const currentlySelectedId = Array.isArray(values?.schedule_ids)
      ? values.schedule_ids[0]
      : undefined;
    if (Number(currentlySelectedId) === Number(s.id)) {
      setFieldValue("schedule_ids", []);
      setFieldValue("schedule", []);
      setFieldValue("time_ids", []);
      setFieldValue("time", []);
      setIncompletePanels((prev) => ({ ...prev, 0: false }));
      setActiveSessionKey(undefined);
      return;
    }
    setFieldValue("schedule_ids", [Number(s.id)]);
    setFieldValue("schedule", [s]);
    setFieldValue("time_ids", []);
    setFieldValue("time", []);
    const selectedDate = s.date;
    const duration =
      _.get(assessmentSchedules, `[${scheduleIdx}].session_duration`) ??
      _.get(assessmentSchedules, `[0].session_duration`);
    if (scheduleIdx !== -1) {
      loadSessionScheduleTime(
        scheduleIdx,
        selectedDate,
        duration,
        values?.psychologist?.id,
        values?.type ?? ""
      );
    }
    setTimeout(() => {
      try {
        const el = document.getElementById("single-session-times");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (err) {
        // ignore
      }
    }, 120);
  };

  const handleSingleSessionTimeChange = (e: any) => {
    const newTimeIds = Array.isArray(values?.time_ids)
      ? [...values.time_ids]
      : [undefined];
    newTimeIds[0] = e.target.value;
    setFieldValue(
      "time_ids",
      (newTimeIds as any[]).filter((id) => typeof id === "number")
    );
    const sid = values.schedule_ids[0];
    const idx = schedules.findIndex((sc) => sc.id === sid);
    setFieldValue(
      "time",
      (sessionScheduleTimes[idx] ?? []).filter((t) => t.id === e.target.value)
    );
  };

  const handleMultiSessionScheduleClick = (s: any) => {
    const exists =
      Array.isArray(values.schedule_ids) &&
      values.schedule_ids.includes(Number(s.id));
    let disabled = false;

    if (
      !exists &&
      Array.isArray(values.schedule_ids) &&
      values.schedule_ids.length > 0
    ) {
      for (let i = 0; i < values.schedule_ids.length; i++) {
        const selectedId = values.schedule_ids[i];
        const selectedSchedule = schedules.find((sc) => sc.id === selectedId);
        const selectedDate = selectedSchedule
          ? new Date(selectedSchedule.date)
          : null;
        const candidateDate = new Date(s.date);
        const minDays = assessmentSchedules?.[i]?.session_min_days || 0;

        if (
          selectedDate &&
          !isNaN(candidateDate.getTime()) &&
          !isNaN(selectedDate.getTime())
        ) {
          const diffDays = Math.abs(
            (candidateDate.getTime() - selectedDate.getTime()) /
              (1000 * 60 * 60 * 24)
          );
          if (diffDays < minDays) {
            disabled = true;
            break;
          }
        }
      }
    }

    if (!exists && disabled) return;

    const scheduleIdx = schedules.findIndex((sc) => sc.id === s.id);
    let newSelected: number[];

    if (exists) {
      // Deselecting: remove this and all later selected sessions (cascade unselect)
      const selectedIdx = values.schedule_ids.findIndex(
        (si: number) => si === s.id
      );
      // Keep only earlier selections
      newSelected = values.schedule_ids.slice(0, selectedIdx);

      setFieldValue("schedule_ids", newSelected);
      setFieldValue(
        "schedule",
        newSelected
          .map((id: number) => schedules.find((sc) => sc.id === id) || null)
          .filter((item): item is typeof schedules[0] => item !== null)
      );

      if (Array.isArray(values.time_ids)) {
        const newTimeIds = values.time_ids.slice(0, selectedIdx);
        setFieldValue("time_ids", newTimeIds);
      } else {
        setFieldValue("time_ids", []);
      }

      if (Array.isArray(values.time)) {
        const newTimes = values.time.slice(0, selectedIdx);
        setFieldValue("time", newTimes);
      } else {
        setFieldValue("time", []);
      }

      setIncompletePanels((prev) => {
        const copy = { ...(prev || {}) };
        for (let i = selectedIdx; i < sessionCount; i++) {
          if (copy.hasOwnProperty(i)) delete copy[i];
        }
        return copy;
      });

      return;
    } else {
      if (values.schedule_ids.length < sessionCount) {
        newSelected = [...values.schedule_ids, s.id];
        const newSelectedWithDate = newSelected
          .map((id) => {
            const sch = schedules.find((sc) => sc.id === id);
            return sch ? { id, date: sch.date } : null;
          })
          .filter(
            (item): item is { id: number; date: string } => item !== null
          );
        newSelected = sortScheduleDateOptions(newSelectedWithDate as any).map(
          (item) => Number(item.id)
        );
      } else {
        message.warning(`You can only select up to ${sessionCount} sessions.`);
        newSelected = values.schedule_ids;
      }
    }

    setFieldValue("schedule_ids", newSelected);
    setFieldValue(
      "schedule",
      newSelected
        .map((id: number) => schedules.find((sc) => sc.id === id) || null)
        .filter((item): item is typeof schedules[0] => item !== null)
    );

    const selectedDate = s.date;
    const duration =
      _.get(assessmentSchedules, `[${scheduleIdx}].session_duration`) ??
      _.get(assessmentSchedules, `[0].session_duration`);

    if (scheduleIdx !== -1) {
      loadSessionScheduleTime(
        scheduleIdx,
        selectedDate,
        duration,
        values?.psychologist?.id,
        values?.type ?? ""
      );
    }

    const sessionIdx = newSelected.findIndex((id) => id === s.id);
    if (sessionIdx !== -1) {
      setActiveSessionKey(sessionIdx.toString());

      setIncompletePanels((prev) => {
        const copy: Record<number, boolean> = { ...(prev || {}) };
        const timeIds = Array.isArray(values?.time_ids) ? values.time_ids : [];
        newSelected.forEach((_, idx) => {
          if (idx < sessionIdx) {
            const hasTime = timeIds[idx] !== undefined && timeIds[idx] !== null;
            if (!hasTime) copy[idx] = true;
            else delete copy[idx];
          } else if (idx === sessionIdx) {
            delete copy[idx];
          }
        });
        return copy;
      });

      setTimeout(() => {
        try {
          const el = document.getElementById(`session-collapse-${sessionIdx}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch {
          // ignore
        }
      }, 120);
    }
  };

  const handleResetAllSchedules = () => {
    clearScheduleData();
    setActiveSessionKey(undefined);
    setIncompletePanels({});
  };

  const makeHandleCollapseChange =
    (sessionIdx: number) => (key: string | string[]) => {
      const isDateSelected = values?.schedule_ids?.[sessionIdx] !== undefined;
      const isTimeSelected =
        Array.isArray(values.time_ids) && !!values.time_ids[sessionIdx];
      if (!isDateSelected) return;
      const keyArr = Array.isArray(key)
        ? key.map((k) => Number(k))
        : [Number(key)];
      const activeArr = Array.isArray(activeSessionKey)
        ? activeSessionKey.map((k) => Number(k))
        : [Number(activeSessionKey)];
      if (activeArr.includes(sessionIdx) && !keyArr.includes(sessionIdx)) {
        setIncompletePanels((prev) => ({
          ...prev,
          [sessionIdx]: !isTimeSelected,
        }));
      }
      if (keyArr.includes(sessionIdx)) {
        setIncompletePanels((prev) => ({ ...prev, [sessionIdx]: false }));
      }
      setActiveSessionKey(key);
    };

  const makeHandleMultiSessionTimeChange = (sessionIdx: number) => (e: any) => {
    const newTimeIds = Array.isArray(values?.time_ids)
      ? [...values.time_ids]
      : Array(sessionCount).fill(undefined);
    newTimeIds[sessionIdx] = e.target.value;
    setFieldValue("time_ids", newTimeIds);

    const scheduleId = values.schedule_ids[sessionIdx];
    const idx = schedules.findIndex((sc) => sc.id === scheduleId);
    const selectedTime = (sessionScheduleTimes[idx] ?? []).find(
      (item) => item.id === e.target.value
    );
    const newTimes = Array.isArray(values?.time)
      ? [...values.time]
      : Array(sessionCount).fill(undefined);
    newTimes[sessionIdx] = selectedTime;
    setFieldValue("time", newTimes);

    setIncompletePanels((prev) => ({ ...prev, [sessionIdx]: false }));
    const nextIdx = sessionIdx + 1;
    if (nextIdx < sessionCount) {
      if (
        Array.isArray(values?.schedule_ids) &&
        values.schedule_ids[nextIdx] !== undefined &&
        values.schedule_ids[nextIdx] !== null
      ) {
        setActiveSessionKey(nextIdx.toString());
      } else {
        setActiveSessionKey(undefined);
      }
    } else {
      setActiveSessionKey(undefined);
    }
  };

  const dateKey = (m: any) =>
    m && typeof m.format === "function" ? m.format("YYYY-MM-DD") : "";
  const getSelectedDates = () =>
    Array.isArray(values?.schedule_ids)
      ? values.schedule_ids
          .map((id: number) => schedules.find((sc) => sc.id === id)?.date)
          .filter((d): d is string => !!d)
      : [];

  const handleCalendarSelectSingle = (d: any) => {
    const dk = dateKey(d);
    if (!dk) return;
    const candidate =
      schedules.find(
        (sc) => sc.date === dk && !values?.schedule_ids?.includes(Number(sc.id))
      ) || schedules.find((sc) => sc.date === dk);
    if (candidate) {
      handleSingleSessionScheduleClick(candidate);
    }
  };

  const handleCalendarSelectMulti = (d: any) => {
    const dk = dateKey(d);
    if (!dk) return;
    let candidate = schedules.find(
      (sc) => sc.date === dk && !values?.schedule_ids?.includes(Number(sc.id))
    );
    if (!candidate) candidate = schedules.find((sc) => sc.date === dk);
    if (candidate) {
      handleMultiSessionScheduleClick(candidate);
    }
  };

  const handleCalendarApplyMulti = (dateKeys: string[]) => {
    if (!Array.isArray(dateKeys) || dateKeys.length === 0) {
      setFieldValue("schedule_ids", []);
      setFieldValue("schedule", []);
      setFieldValue("time_ids", []);
      setFieldValue("time", []);
      return;
    }

    const uniqueSortedKeys = Array.from(new Set(dateKeys)).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    const candidates = uniqueSortedKeys
      .map((dk) => schedules.find((sc) => sc.date === dk) || null)
      .filter((s): s is typeof schedules[0] => !!s);

    const sortedCandidates = sortScheduleDateOptions(candidates);
    if (candidates.length === 0) return;

    const minDaysArr = assessmentSchedules.map((s) => s.session_min_days || 0);
    const chosen: typeof candidates = [];
    for (const s of sortedCandidates) {
      if (chosen.length >= sessionCount) break;

      if (chosen.length > 0) {
        const prev = chosen[chosen.length - 1];
        const minDays = minDaysArr[chosen.length - 1] || 0;
        const diffDays = Math.round(
          (new Date(s.date).getTime() - new Date(prev.date).getTime()) /
            (1000 * 60 * 60 * 24)
        );
        if (diffDays < minDays) continue;
      }

      chosen.push(s);
    }

    if (chosen.length === 0) return;

    const prevSchedules = values?.schedule ?? [];
    const prevTimes = values?.time ?? [];
    const prevTimeIds = values?.time_ids ?? [];

    const newTimes: any[] = [];
    const newTimeIds: any[] = [];

    for (const s of chosen) {
      const idx = prevSchedules.findIndex((p) => Number(p.id) === Number(s.id));
      if (idx !== -1) {
        newTimes.push(prevTimes[idx]);
        newTimeIds.push(prevTimeIds[idx]);
      } else {
        newTimes.push(undefined);
        newTimeIds.push(undefined);
      }
    }

    const selectedIds = chosen.map((s) => Number(s.id));
    setFieldValue("schedule_ids", selectedIds);
    setFieldValue("schedule", chosen);
    setFieldValue("time_ids", newTimeIds);
    setFieldValue("time", newTimes);

    const appliedChosen = sortScheduleDateOptions(chosen);
    appliedChosen.forEach((s) => {
      const scheduleIdx = schedules.findIndex(
        (sc) => Number(sc.id) === Number(s.id)
      );
      const isNew = !prevSchedules.some((p) => Number(p.id) === Number(s.id));

      if (scheduleIdx !== -1 && isNew) {
        const duration =
          _.get(assessmentSchedules, `[${scheduleIdx}].session_duration`) ??
          _.get(assessmentSchedules, `[0].session_duration`);

        loadSessionScheduleTime(
          scheduleIdx,
          s.date,
          duration,
          values?.psychologist?.id,
          values?.type ?? ""
        );
      }
    });

    const requested = new Set(uniqueSortedKeys);
    const applied = new Set(chosen.map((c) => c.date));
    const dropped = Array.from(requested).filter((k) => !applied.has(k));
    if (dropped.length > 0) {
      message.warning("Some dates were skipped due to minimum day gaps.");
    }
  };

  const handleContinueAsGuest = () => {
    setContinueAsGuest(true);
    setFieldValue("continueAsGuest", true);
  };

  const handleLoginOrSignup = () => {
    saveBookingAssessmentInfo(form.values);
    localStorage.setItem("redirectToBooking", "assessment");
    navigate("/auth/login");
  };

  const handleAddPatient = () => setPatientModalVisible({} as Patient);
  const handleGuestFormValuesChange = (changedValues: any) => {
    if (changedValues.patient) {
      setFieldValue("patient", {
        ...values.patient,
        ...changedValues.patient,
      });
    }
  };

  return (
    <>
      {!!alertMessage && (
        <Alert
          className="alert-success"
          type="success"
          message={alertMessage}
          banner
          showIcon={false}
        />
      )}
      <PatientModal
        data={showPatientModal?.id ? showPatientModal : null}
        visible={!!showPatientModal}
        onSubmit={handlePatientModalSubmit}
        onClose={handleClosePatientForm}
        title={
          showPatientModal?.id
            ? t("common.text.completeYourProfile")
            : undefined
        }
        showNotification={false}
      />

      <Row gutter={[20, 20]} id="booking-assessment-step-patient">
        <Col md={7} xs={24}>
          <Space size={10} direction="vertical" style={{ width: "100%" }}>
            <VuiFormCard className="assessment-info">
              <div className="avatar-wrapper">
                <img
                  className="avatar-image"
                  src={values?.assessment?.photo?.url}
                  alt={values?.assessment?.name || "Assessment"}
                />
              </div>
              <div className="assessment-info-wrapper">
                <label>{t("common.text.package")}</label>
                <div className="assessment-info-name">
                  {values?.assessment?.name}
                </div>
              </div>
            </VuiFormCard>
            {hasPsychologist && (
              <VuiFormCard className="psychologist-info">
                <div className="psychologist-info-wrapper">
                  <label>{t("common.text.psychologist")}</label>
                  <div className="psychologist-info-name">
                    {values?.psychologist?.name}
                  </div>
                </div>

                <VuiButton
                  buttonProps={{
                    type: "primary",
                    className: "vui-button primary-outline",
                    icon: <LeftOutlined />,
                    onClick: handleChangePsychologist,
                  }}
                  label={t("common.button.changePsychologist")}
                />
              </VuiFormCard>
            )}
          </Space>
        </Col>

        <Col md={17} xs={24}>
          <VuiFormCard>
            {hasPsychologist && (
              <>
                <Row gutter={[20, 20]} justify="space-between" align="middle">
                  <Space
                    direction="vertical"
                    size={20}
                    style={{ width: "100%" }}
                  >
                    <Col xs={24}>
                      <label className="section-title">
                        {t("common.text.assessmentAndProgramsMethod")}
                      </label>
                    </Col>
                    <Col xs={24}>
                      <Row gutter={[20, 20]}>
                        <Col md={8} xs={24}>
                          <SessionCard
                            isActive={values?.type === "Online"}
                            onClick={handleSelectOnlineSession}
                            label={
                              <>
                                <span>{t("common.text.online")}</span>
                                <span className="note-inline">
                                  {t("common.text.byGoogleMeet")}
                                </span>
                              </>
                            }
                          />
                        </Col>

                        <Col md={8} xs={24}>
                          <SessionCard
                            isActive={values?.type === "Offline"}
                            onClick={handleSelectOfflineSession}
                            label={
                              <>
                                <span>{t("common.text.faceToFace")}</span>
                                <span className="note-inline">
                                  {t("common.text.atOurOffice")}
                                </span>
                              </>
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Space>
                </Row>
                <Divider style={{ marginTop: 40, marginBottom: 40 }} />
                <>
                  {isFetching || !schedules || !assessmentSchedules ? (
                    <VuiContentLoading loading={isFetching} />
                  ) : isSingleSession ? (
                    <Space
                      direction="vertical"
                      size={20}
                      style={{ width: "100%" }}
                    >
                      {sortedSchedules?.length > 0 ? (
                        <>
                          <Row gutter={[20, 20]}>
                            <Col xs={24}>
                              <label className="section-title">
                                {t("common.text.selectDateAndTime")}
                              </label>
                            </Col>
                            <Col xs={24}>
                              <Row gutter={[8, 8]}>
                                <Col xs={24}>
                                  <label className="field-title">
                                    {t("common.text.date")}
                                  </label>
                                </Col>
                                <Col xs={24}>
                                  <Row gutter={[20, 20]}>
                                    <Col
                                      xs={24}
                                      className="schedule-chips-col"
                                      flex="1 1 0%"
                                    >
                                      <Row gutter={[12, 12]}>
                                        {sortedSchedules
                                          .slice(0, 6)
                                          .map((schedule) => (
                                            <Col
                                              md={8}
                                              xs={12}
                                              key={schedule.id}
                                            >
                                              <ScheduleChip
                                                onClick={
                                                  handleSingleSessionScheduleClick
                                                }
                                                value={schedule}
                                                isSelected={values?.schedule_ids?.includes(
                                                  Number(schedule?.id)
                                                )}
                                              />
                                            </Col>
                                          ))}
                                      </Row>
                                    </Col>
                                    <Col
                                      xs={24}
                                      className="schedule-calendar-col"
                                      flex="0 0 auto"
                                    >
                                      <AssessmentScheduleCalendar
                                        data={sortedSchedules}
                                        selectedData={getSelectedDates()}
                                        onSelectDate={
                                          handleCalendarSelectSingle
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            </Col>
                          </Row>

                          {Array.isArray(values?.schedule_ids) &&
                            values.schedule_ids.length > 0 &&
                            values.schedule_ids[0] && (
                              <Row gutter={[20, 20]}>
                                <Col xs={24}>
                                  <Row gutter={[8, 8]}>
                                    {values?.schedule_ids &&
                                      values.schedule_ids.length > 0 &&
                                      values.schedule_ids[0] && (
                                        <Col xs={24}>
                                          <label className="field-title">
                                            {t("common.text.time")}
                                          </label>
                                        </Col>
                                      )}
                                    <Col xs={24} id="single-session-times">
                                      {fetchingSessionScheduleTimeIdx !==
                                      undefined ? (
                                        <Skeleton.Button active block />
                                      ) : (
                                        <Radio.Group
                                          value={
                                            Array.isArray(values?.time_ids)
                                              ? values.time_ids[0]
                                              : undefined
                                          }
                                          className="assessment-time-select"
                                          onChange={
                                            handleSingleSessionTimeChange
                                          }
                                        >
                                          {(() => {
                                            const sid = values.schedule_ids[0];
                                            const idx = schedules.findIndex(
                                              (sc) => sc.id === sid
                                            );
                                            return sortScheduleTimeOptions(
                                              sessionScheduleTimes[idx] ?? []
                                            ).map((item) => (
                                              <Radio.Button
                                                key={item.id}
                                                value={item.id}
                                                disabled={!item.is_available}
                                              >
                                                {item.label}
                                              </Radio.Button>
                                            ));
                                          })()}
                                        </Radio.Group>
                                      )}
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            )}
                        </>
                      ) : (
                        <Row gutter={[20, 20]}>
                          <Col xs={24}>
                            <Typography.Text style={{ color: "red" }}>
                              {t("common.text.noScheduleAvailable")}
                            </Typography.Text>
                          </Col>
                        </Row>
                      )}
                    </Space>
                  ) : (
                    <Space
                      direction="vertical"
                      size={20}
                      style={{ width: "100%" }}
                    >
                      {sortedSchedules.length > 0 ? (
                        <Row gutter={[20, 20]}>
                          <Col xs={24}>
                            <label className="section-title">
                              {t("common.text.selectDates")}
                            </label>
                            <div>
                              <label className="helper-text">
                                {t(
                                  "common.text.sessionsAreScheduledWithTimeGaps"
                                )}
                              </label>
                            </div>
                          </Col>

                          <Col xs={24}>
                            <Row gutter={[8, 8]}>
                              <Col xs={24}>
                                <Row gutter={[20, 20]}>
                                  <Col
                                    xs={24}
                                    className="schedule-chips-col"
                                    flex="1 1 0%"
                                  >
                                    <Row gutter={[12, 12]}>
                                      {sortedSchedules
                                        .slice(0, 6)
                                        .map((schedule) => (
                                          <Col md={8} xs={12} key={schedule.id}>
                                            <ScheduleChip
                                              onClick={
                                                handleMultiSessionScheduleClick
                                              }
                                              value={schedule}
                                              isSelected={values?.schedule_ids?.includes(
                                                Number(schedule?.id)
                                              )}
                                              disabled={isScheduleDisabled(
                                                schedule
                                              )}
                                            />
                                          </Col>
                                        ))}
                                    </Row>
                                  </Col>
                                  <Col
                                    xs={24}
                                    className="schedule-calendar-col"
                                    flex="0 0 auto"
                                  >
                                    <AssessmentScheduleCalendar
                                      data={sortedSchedules}
                                      selectedData={getSelectedDates()}
                                      onSelectDate={handleCalendarSelectMulti}
                                      multiSelect
                                      selectionLimit={sessionCount}
                                      minDaysBetween={assessmentSchedules.map(
                                        (s) => s.session_min_days || 0
                                      )}
                                      onApplyDates={handleCalendarApplyMulti}
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Col>

                          <Col xs={24} style={{ marginTop: 20 }}>
                            <Row
                              className="select-times-header"
                              align="middle"
                              justify="space-between"
                              style={{ width: "100%" }}
                            >
                              <Col>
                                <label className="section-title">
                                  {t("common.text.selectTimes")}
                                </label>
                              </Col>
                              <Col>
                                <button
                                  className="plain-btn"
                                  onClick={handleResetAllSchedules}
                                >
                                  <Space size={4}>
                                    <ReloadOutlined style={{ fontSize: 14 }} />
                                    Reset all
                                  </Space>
                                </button>
                              </Col>
                            </Row>
                          </Col>

                          <Col xs={24}>
                            <Space
                              direction="vertical"
                              size={20}
                              style={{ width: "100%" }}
                            >
                              {Array.from({ length: sessionCount }).map(
                                (_, sessionIdx) => {
                                  const isDateSelected =
                                    values?.schedule_ids?.[sessionIdx] !==
                                    undefined;
                                  const isTimeSelected =
                                    Array.isArray(values.time_ids) &&
                                    !!values.time_ids[sessionIdx];
                                  const isIncomplete =
                                    incompletePanels[sessionIdx];

                                  const collapseClass = isIncomplete
                                    ? "ant-collapse-incomplete"
                                    : "";

                                  return (
                                    <Collapse
                                      key={sessionIdx}
                                      activeKey={activeSessionKey}
                                      onChange={makeHandleCollapseChange(
                                        sessionIdx
                                      )}
                                      expandIconPosition="end"
                                      className={collapseClass}
                                      expandIcon={({ isActive }) => (
                                        <ArrowDownIcon
                                          className="collapse-arrow-icon"
                                          style={{
                                            transform: isActive
                                              ? "rotate(180deg)"
                                              : "rotate(0deg)",
                                          }}
                                        />
                                      )}
                                    >
                                      <Collapse.Panel
                                        disabled={!isDateSelected}
                                        key={sessionIdx}
                                        showArrow
                                        header={
                                          <Row className="collapse-panel-header-row">
                                            <Col>
                                              <Row
                                                className="collapse-panel-header-info"
                                                gutter={12}
                                              >
                                                <Col
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                      "space-between",
                                                    gap: 6,
                                                  }}
                                                >
                                                  <span className="collapse-panel-header-session">{`Session ${
                                                    sessionIdx + 1
                                                  }`}</span>
                                                  {isTimeSelected ? (
                                                    <CheckCircleFilledIcon />
                                                  ) : isIncomplete ? (
                                                    <InfoCircleOutlined
                                                      style={{
                                                        color: "#FF290B",
                                                        fontSize: 18,
                                                      }}
                                                    />
                                                  ) : null}
                                                </Col>
                                                <Col>
                                                  <Divider
                                                    type="vertical"
                                                    className="collapse-panel-header-divider"
                                                  />
                                                </Col>
                                                <Col className="collapse-panel-header-date">
                                                  {isDateSelected
                                                    ? (() => {
                                                        const scheduleId =
                                                          values.schedule_ids[
                                                            sessionIdx
                                                          ];
                                                        const schedule =
                                                          schedules.find(
                                                            (sc) =>
                                                              sc.id ===
                                                              scheduleId
                                                          );
                                                        let timeLabel = "";
                                                        if (
                                                          Array.isArray(
                                                            values.time_ids
                                                          ) &&
                                                          values.time_ids[
                                                            sessionIdx
                                                          ]
                                                        ) {
                                                          const timeId =
                                                            values.time_ids[
                                                              sessionIdx
                                                            ];
                                                          const idx =
                                                            schedules.findIndex(
                                                              (sc) =>
                                                                sc.id ===
                                                                scheduleId
                                                            );
                                                          const timeOption =
                                                            sortScheduleTimeOptions(
                                                              sessionScheduleTimes[
                                                                idx
                                                              ] ?? []
                                                            ).find(
                                                              (item) =>
                                                                item.id ===
                                                                timeId
                                                            );
                                                          if (timeOption)
                                                            timeLabel =
                                                              timeOption.label;
                                                        }
                                                        if (!schedule?.date)
                                                          return "Please select a date";
                                                        return `${formatDate(
                                                          schedule.date
                                                        )}${
                                                          timeLabel
                                                            ? " - " +
                                                              timeLabel +
                                                              " WIB"
                                                            : ""
                                                        }`;
                                                      })()
                                                    : "Please select a date"}
                                                </Col>
                                              </Row>
                                            </Col>
                                            <Col className="collapse-panel-header-meta">
                                              {sessionIdx < sessionCount - 1 &&
                                                (() => {
                                                  const minDays =
                                                    assessmentSchedules?.[
                                                      sessionIdx
                                                    ]?.session_min_days;
                                                  if (!minDays || minDays === 0)
                                                    return null;
                                                  if (minDays % 7 === 0) {
                                                    const weeks = minDays / 7;
                                                    return (
                                                      <span>
                                                        Next session in{" "}
                                                        <b>
                                                          {weeks} week
                                                          {weeks > 1 ? "s" : ""}
                                                        </b>
                                                      </span>
                                                    );
                                                  }
                                                  if (minDays === 1)
                                                    return (
                                                      <span>
                                                        Next session in{" "}
                                                        <b>1 day</b>
                                                      </span>
                                                    );
                                                  return (
                                                    <span>
                                                      Next session in{" "}
                                                      <b>{minDays} days</b>
                                                      {isMobile
                                                        ? "\u00A0-\u00A0"
                                                        : " "}{" "}
                                                    </span>
                                                  );
                                                })()}
                                              <span className="collapse-panel-header-duration">
                                                {assessmentSchedules?.[
                                                  sessionIdx
                                                ]?.session_duration
                                                  ? `${assessmentSchedules[sessionIdx].session_duration} minutes`
                                                  : "Session"}
                                              </span>
                                            </Col>
                                          </Row>
                                        }
                                      >
                                        <div
                                          id={`session-collapse-${sessionIdx}`}
                                        />
                                        <Radio.Group
                                          value={
                                            Array.isArray(values?.time_ids)
                                              ? values.time_ids[sessionIdx]
                                              : undefined
                                          }
                                          className="assessment-time-select"
                                          onChange={makeHandleMultiSessionTimeChange(
                                            sessionIdx
                                          )}
                                        >
                                          {isDateSelected &&
                                            (() => {
                                              const scheduleId =
                                                values.schedule_ids[sessionIdx];
                                              const idx = schedules.findIndex(
                                                (sc) => sc.id === scheduleId
                                              );
                                              return fetchingSessionScheduleTimeIdx !==
                                                undefined &&
                                                fetchingSessionScheduleTimeIdx ===
                                                  idx ? (
                                                <Skeleton.Button active block />
                                              ) : (
                                                sortScheduleTimeOptions(
                                                  sessionScheduleTimes[idx] ??
                                                    []
                                                ).map((item) => (
                                                  <Radio.Button
                                                    key={item.id}
                                                    value={item.id}
                                                    disabled={
                                                      !item.is_available
                                                    }
                                                  >
                                                    {item.label}
                                                  </Radio.Button>
                                                ))
                                              );
                                            })()}
                                        </Radio.Group>
                                      </Collapse.Panel>
                                    </Collapse>
                                  );
                                }
                              )}
                            </Space>
                          </Col>
                        </Row>
                      ) : (
                        <Row gutter={[20, 20]}>
                          <Col xs={24}>
                            <Typography.Text style={{ color: "red" }}>
                              {t("common.text.noScheduleAvailable")}
                            </Typography.Text>
                          </Col>
                        </Row>
                      )}
                    </Space>
                  )}
                </>
                <Divider style={{ marginTop: 40, marginBottom: 40 }} />
              </>
            )}

            {isLoadingPatients ? (
              <VuiContentLoading loading={isLoadingPatients} />
            ) : patients.length > 0 ? (
              <>
                <Row gutter={[20, 20]} justify="space-between" align="middle">
                  <Col xs={24} lg={12}>
                    <div className="patient-section-title">
                      {t("common.text.choosePatient")}
                    </div>
                  </Col>
                  <Col
                    xs={{ span: 16, offset: 4 }}
                    lg={6}
                    style={{ textAlign: "right" }}
                  >
                    <VuiButton
                      buttonProps={{
                        type: "primary",
                        className: "vui-button primary-outline",
                        icon: <PlusOutlined />,
                        onClick: handleAddPatient,
                      }}
                      label={t("common.button.addPatient")}
                    />
                  </Col>
                </Row>

                <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
                  {patients.map((patient) => (
                    <Col xs={24} lg={12} key={patient.id}>
                      <PatientCard
                        data={patient}
                        isSelected={values?.patient?.id === patient.id}
                        onSelect={() => handleSelectPatient(patient)}
                        onCompleteProfile={() => handleShowPatientForm(patient)}
                      />
                    </Col>
                  ))}
                </Row>
              </>
            ) : (
              <Row gutter={[20, 20]} align="middle">
                <Col xs={24}>
                  <div className="patient-section-title">
                    {t("common.text.patientForm")}
                  </div>
                </Col>
                <Col xs={24}>
                  <Space
                    direction="vertical"
                    size={20}
                    align="center"
                    style={{ width: "100%" }}
                  >
                    <Row align="middle">
                      <Col xs={24} style={{ textAlign: "center" }}>
                        <span className="basp-muted">
                          {t("common.text.continueAsAGuestToFillInYourDetails")}
                        </span>
                      </Col>
                    </Row>
                    <Space>
                      <VuiButton
                        buttonProps={{
                          type: "primary",
                          className: "vui-button primary-outline",
                          onClick: handleContinueAsGuest,
                        }}
                        label={t("common.button.continueAsGuest")}
                      />
                      <VuiButton
                        buttonProps={{
                          type: "primary",
                          className: "vui-button primary",
                          onClick: handleLoginOrSignup,
                        }}
                        label={t("common.button.logInOrSignup")}
                      />
                    </Space>
                  </Space>
                </Col>

                {continueAsGuest && (
                  <>
                    <Divider style={{ marginTop: 30, marginBottom: 20 }} />
                    <Col xs={24}>
                      <Space
                        direction="vertical"
                        size={20}
                        style={{ width: "100%" }}
                      >
                        <Row align="middle">
                          <Col xs={24} style={{ textAlign: "center" }}>
                            <span className="basp-muted">
                              {t("common.text.kindlyFillOutTheFormBelow")}
                            </span>
                          </Col>
                        </Row>
                        <Form
                          form={formGuest}
                          layout="vertical"
                          onValuesChange={handleGuestFormValuesChange}
                        >
                          <Row gutter={[20, 20]}>
                            <Col xs={24}>
                              <Form.Item
                                name={["patient", "name"]}
                                label={t("common.text.patientName")}
                              >
                                <Input
                                  placeholder={t(
                                    "common.form.fullName.placeholder"
                                  )}
                                />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                name={["patient", "gender"]}
                                label={t("common.text.patientGender")}
                              >
                                <Radio.Group>
                                  <Radio value="Laki-laki">
                                    {t("common.text.male")}
                                  </Radio>
                                  <Radio value="Perempuan">
                                    {t("common.text.female")}
                                  </Radio>
                                </Radio.Group>
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                name={["patient", "date_of_birth"]}
                                label={t("common.text.patientDateOfBirth")}
                              >
                                <VuiDatePicker />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                validateStatus={emailValidation.validateStatus}
                                help={emailValidation.errorMessage}
                                name={["patient", "email"]}
                                label={t("common.text.patientEmail")}
                              >
                                <Input
                                  placeholder={t(
                                    "common.form.email.placeholder"
                                  )}
                                />
                              </Form.Item>
                            </Col>

                            <Col xs={24} md={12}>
                              <Form.Item
                                initialValue={{
                                  code: DEFAULT_PHONE_COUNTRY_CODE,
                                }}
                                name={["patient", "phone_number"]}
                                label={t("common.text.patientPhoneNumber")}
                                validateStatus={phoneValidation.validateStatus}
                                help={phoneValidation.errorMessage}
                              >
                                <PhoneInput
                                  placeholder={t(
                                    "common.form.phone.placeholder"
                                  )}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form>
                      </Space>
                      <Typography.Paragraph
                        style={{
                          color: "#C6C8CC",
                          fontSize: 14,
                          marginTop: 6,
                        }}
                      >
                        *{t("common.text.emailPhoneDisclaimer")}
                      </Typography.Paragraph>
                    </Col>
                  </>
                )}
              </Row>
            )}
          </VuiFormCard>
        </Col>
      </Row>
    </>
  );
};

export default BookingAssessmentStepPatient;
